/**
 * @file core.js
 * @description Core logic for the Interactive Cyber Portfolio OS.
 * (FIXED: Init order ensures URL parsing before UI bootstrap)
 */

import { Kernel } from './kernel.js';
import { PortfolioUI } from '../modules/gui/gui.js';
import { TerminalInterface } from '../modules/terminal/terminal.js';

class CyberOS {
    constructor() {
        this.dom = {
            guiWrapper: document.getElementById('gui-container-wrapper'),
            guiContainer: null 
        };

        this.kernel = new Kernel();
        this.eventBus = this.kernel.get('eventBus');
        this.stateManager = this.kernel.get('state');
        
        // ✅ Initialize state FIRST (but don't parse URL yet)
        this.stateManager.setState({ mode: 'GUI' }, true);

        this.portfolioUI = new PortfolioUI(this.kernel);
        this.terminalInterface = new TerminalInterface(this.kernel);
        
        this.init();
    }

    async init() {
        // 1. Load HTML & Content
        await this.portfolioUI.loadHtml();
        await this.terminalInterface.loadHtml();
        this.dom.guiContainer = document.getElementById('gui-container');
        await this.kernel.get('content').loadContent();

        // 2. Initialize Theme (must happen before GUI render)
        this.kernel.get('theme').init();
        
        // ✅ CRITICAL FIX: Parse URL BEFORE initializing UI
        this.kernel.get('state').init(); 
        
        // 3. Initialize UI Components
        this.portfolioUI.init();
        this.terminalInterface.init();
        
        // 4. SEO Injection
        this.generateSchemaData();
        this.setupEventListeners();

        // 5. === BOOT SEQUENCE ===
        const isMobile = this.stateManager.get('isMobile');
        console.log(`[Core] System Ready. Mobile: ${isMobile}`);

        // Ensure a theme is set
        if (!localStorage.getItem('cyber-os-theme')) {
            await this.kernel.get('theme').setTheme('aegis', true);
        }

        // Launch GUI
        this.showGui();
        
        // ✅ BOOTSTRAP NAVIGATION (After all components are ready)
        const currentPath = this.stateManager.get('path');
        if (currentPath && currentPath.length > 0) {
            const section = currentPath[0];
            const detailId = currentPath[1] || null;
            console.log(`[Core] Deep link detected: ${section}/${detailId || ''}`);
            this.portfolioUI.showPage(section, detailId);
        } else {
            this.portfolioUI.showPage('home');
        }

        // Mobile specific adjustments
        if (isMobile) {
            this.stateManager.setState({ ui: { sidebarOpen: false } });
        }
    }

    generateSchemaData() {
        const content = this.kernel.get('content');
        const profile = content.getProfile();
        const contact = content.getContact();
        const projects = content.getProjects();

        const softwareApplications = projects.map(p => ({
            "@type": "SoftwareApplication",
            "name": p.title,
            "description": p.excerpt,
            "applicationCategory": "DeveloperTool",
            "operatingSystem": "Web Browser",
            "url": p.links?.demo || p.links?.github,
            "offers": {
                "@type": "Offer",
                "price": "0",
                "priceCurrency": "USD"
            },
            "aggregateRating": {
                "@type": "AggregateRating",
                "ratingValue": "5",
                "ratingCount": "1"
            },
            "keywords": p.tags ? p.tags.join(', ') : ""
        }));
        
        const schema = {
            "@context": "https://schema.org",
            "@type": "Person",
            "name": profile.name,
            "jobTitle": profile.role,
            "url": window.location.href,
            "hasOfferCatalog": {
                "@type": "OfferCatalog",
                "name": "Portfolio Projects",
                "itemListElement": softwareApplications
            },
            "contactPoint": [{
                "@type": "ContactPoint",
                "email": contact.email,
                "contactType": "professional"
            }],
            "sameAs": [contact.github, contact.linkedin].filter(Boolean)
        };

        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.textContent = JSON.stringify(schema);
        document.head.appendChild(script);
        console.log('[Core] JSON-LD Schema injected for AI agents.');
    }

    setupEventListeners() {
        console.log('[Core] Event listeners set up.');
    }

    showGui() {
        console.log('[Core] showGui() called.');
        
        if (this.dom.guiContainer) {
            this.dom.guiContainer.classList.add('active');
        } else {
            console.error('[Core] showGui: #gui-container not found!');
        }
    }
}

document.addEventListener('DOMContentLoaded', () => {
  window.cyberOS = new CyberOS();
});